/// <reference types="vite/client" />

import { viteImageAssetMap } from "virtual:vite-image-assets";
import { type ViteImageManifestEntry } from "@allwyn/build/vite/vite-image-shared";

export interface ResolvedImage {
    src: string;
    fallbackType?: string;
    avifSrc?: string;
    avifSrcset?: string;
}

export interface ViteImageSource {
    src: string;
    media: string;
}

export interface ResolvedImageSource extends ResolvedImage {
    media: string;
}

const mimeTypesByExtension: Record<string, string> = {
    avif: "image/avif",
    gif: "image/gif",
    jpeg: "image/jpeg",
    jpg: "image/jpeg",
    png: "image/png",
    svg: "image/svg+xml",
    webp: "image/webp",
};

export function resolveViteImageAsset(assetPath: string): ResolvedImage {
    const key = `src/${assetPath}`;
    const entry = viteImageAssetMap[key];

    if (!entry) {
        throw new Error(`Missing image asset: "${assetPath}" (key: "${key}")`);
    }

    return resolveImageEntry(entry);
}

export function resolveViteImageSources(sources?: readonly ViteImageSource[] | null): ResolvedImageSource[] {
    return (sources ?? []).map((source) => ({
        ...resolveViteImageAsset(source.src),
        media: source.media,
    }));
}

function resolveImageEntry(entry: ViteImageManifestEntry): ResolvedImage {
    return {
        src: entry.file,
        fallbackType: resolveMimeType(entry.file),
        avifSrc: entry.avifFile,
        avifSrcset: entry.avifFile ? buildSrcSet(entry.avifFile, entry.avifVariants ?? []) : undefined,
    };
}

function buildSrcSet(file: string, variants: Array<{ density: string; file: string }>): string {
    return variants.length > 0
        ? [`${file} 1x`, ...variants.map((variant) => `${variant.file} ${variant.density}x`)].join(", ")
        : file;
}

function resolveMimeType(file: string): string | undefined {
    const extension = file.toLowerCase().split("?", 1)[0].split(".").pop();
    return extension ? mimeTypesByExtension[extension] : undefined;
}
